import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'

// Clean-dropdown replacement for the free-text delivery_method field on a Pre-Devanning Plan
// item (Gary, 2026-09-24) -- reuses pricing_delivery_methods (Z80) rather than a new
// warehouse-only table, since quoting and actual delivery method should be one vocabulary.
// "Other" + free text is kept for the real exceptions that will always exist -- this isn't a
// closed system, staff can still describe something the lookup doesn't cover yet.
//
// Picking a clean option still writes the option's name into delivery_method (TEXT) alongside
// delivery_method_id -- same denormalized-snapshot-plus-FK convention as haulier/
// haulier_company_id (Devannings/AddressPicker.tsx): every existing read-only display of
// devanning_items.delivery_method across the app (Report.tsx, Workflow.tsx, Inbound, etc.)
// keeps working unchanged, and delivery_method_id is the one new authoritative field the
// fulfilment-type lookup (lib/fulfilmentType.ts) actually reads.
export interface DeliveryMethodOption {
  id: string
  code: string
  name: string
  default_fulfilment_type: 'COLLECTION_ONLY' | 'COLLECTION_AND_DELIVERY' | null
}

const FULFILMENT_LABEL: Record<string, string> = {
  COLLECTION_ONLY: 'Collection Only',
  COLLECTION_AND_DELIVERY: 'Collection + Delivery',
}

let cachedOptions: DeliveryMethodOption[] | null = null

export function DeliveryMethodSelect({ methodId, freeText, onChange, className }: {
  methodId: string | null
  freeText: string | null
  onChange: (v: { delivery_method_id: string | null; delivery_method: string | null }) => void
  className?: string
}) {
  const [options, setOptions] = useState<DeliveryMethodOption[]>(cachedOptions || [])

  useEffect(() => {
    if (cachedOptions) return
    supabase.from('pricing_delivery_methods').select('id, code, name, default_fulfilment_type')
      .eq('is_active', true).order('name').then(({ data }) => {
        cachedOptions = (data as DeliveryMethodOption[] | null) || []
        setOptions(cachedOptions)
      })
  }, [])

  const isOther = !methodId

  return (
    <div className={className}>
      <select className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
        value={methodId || '__other__'}
        onChange={e => {
          if (e.target.value === '__other__') { onChange({ delivery_method_id: null, delivery_method: freeText }); return }
          const picked = options.find(o => o.id === e.target.value)
          onChange({ delivery_method_id: e.target.value, delivery_method: picked?.name || null })
        }}>
        {options.map(o => (
          <option key={o.id} value={o.id}>
            {o.name}{o.default_fulfilment_type ? ` — ${FULFILMENT_LABEL[o.default_fulfilment_type]}` : ' — needs confirmation'}
          </option>
        ))}
        <option value="__other__">Other (free text)…</option>
      </select>
      {isOther && (
        <input className="mt-1 w-full border rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
          value={freeText || ''} onChange={e => onChange({ delivery_method_id: null, delivery_method: e.target.value })}
          placeholder="Describe the delivery method…" />
      )}
    </div>
  )
}
